import { API_BASE_URL } from './api';
import { authService } from './authService';
import type {
  ScheduledDive,
  CreateScheduledDiveRequest,
  UpdateScheduledDiveRequest,
  RespondToInviteRequest,
} from '../types/ScheduledDive';

export class ScheduledDivesApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ScheduledDivesApiError';
    this.status = status;
  }
}

const throwApiError = async (response: Response, fallback: string): Promise<never> => {
  const payload = await response.json().catch(() => null);
  throw new ScheduledDivesApiError(payload?.error || fallback, response.status);
};

class ScheduledDivesService {
  async getMyScheduledDives(): Promise<ScheduledDive[]> {
    const response = await fetch(`${API_BASE_URL}/scheduled-dives`, {
      headers: { 'Content-Type': 'application/json', ...authService.getAuthHeaders() },
    });
    if (!response.ok) await throwApiError(response, 'Failed to fetch scheduled dives');
    return response.json();
  }

  async createScheduledDive(request: CreateScheduledDiveRequest): Promise<ScheduledDive> {
    const response = await fetch(`${API_BASE_URL}/scheduled-dives`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authService.getAuthHeaders() },
      body: JSON.stringify(request),
    });
    if (!response.ok) await throwApiError(response, 'Failed to create scheduled dive');
    return response.json();
  }

  async updateScheduledDive(diveId: string, request: UpdateScheduledDiveRequest): Promise<ScheduledDive> {
    const response = await fetch(`${API_BASE_URL}/scheduled-dives/${encodeURIComponent(diveId)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authService.getAuthHeaders() },
      body: JSON.stringify(request),
    });
    if (!response.ok) await throwApiError(response, 'Failed to update scheduled dive');
    return response.json();
  }

  async deleteScheduledDive(diveId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/scheduled-dives/${encodeURIComponent(diveId)}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', ...authService.getAuthHeaders() },
    });
    if (!response.ok) await throwApiError(response, 'Failed to delete scheduled dive');
  }

  async getByInviteCode(inviteCode: string): Promise<ScheduledDive> {
    const response = await fetch(`${API_BASE_URL}/scheduled-dives/invite/${encodeURIComponent(inviteCode)}`, {
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) await throwApiError(response, 'Invite not found or expired');
    return response.json();
  }

  async respondToInvite(inviteCode: string, request: RespondToInviteRequest): Promise<ScheduledDive> {
    const response = await fetch(`${API_BASE_URL}/scheduled-dives/invite/${encodeURIComponent(inviteCode)}/respond`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authService.getAuthHeaders() },
      body: JSON.stringify(request),
    });
    if (!response.ok) await throwApiError(response, 'Failed to respond to invite');
    return response.json();
  }

  buildInviteUrl(inviteCode: string): string {
    return `${window.location.origin}/calendar/join/${inviteCode}`;
  }

  async getOpenDivesForGroup(groupId: string): Promise<ScheduledDive[]> {
    const response = await fetch(`${API_BASE_URL}/scheduled-dives/group/${encodeURIComponent(groupId)}/open`, {
      headers: { 'Content-Type': 'application/json', ...authService.getAuthHeaders() },
    });
    if (!response.ok) await throwApiError(response, 'Failed to fetch open dives');
    return response.json();
  }

  async getOpenDivesForClub(clubId: string): Promise<ScheduledDive[]> {
    const response = await fetch(`${API_BASE_URL}/scheduled-dives/club/${encodeURIComponent(clubId)}/open`, {
      headers: { 'Content-Type': 'application/json', ...authService.getAuthHeaders() },
    });
    if (!response.ok) await throwApiError(response, 'Failed to fetch open dives');
    return response.json();
  }

  async joinOpenDive(diveId: string): Promise<ScheduledDive> {
    const response = await fetch(`${API_BASE_URL}/scheduled-dives/${encodeURIComponent(diveId)}/join`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authService.getAuthHeaders() },
    });
    if (!response.ok) await throwApiError(response, 'Failed to join dive');
    return response.json();
  }
}

export const scheduledDivesService = new ScheduledDivesService();
